import { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { useAppSelector } from '@/app/store/hooks';
import { LazyMasterScreenStyles } from '@/features/masters';
import { listHierarchyOptions } from '../../overview/api/templateHierarchyApi';
import type { HierarchyIssues, HierarchyLevel, HierarchyNode, HierarchyScope } from '../../overview/models/hierarchyScope';
import { HIERARCHY_LEVELS, HIERARCHY_LEVEL_LABEL, SCOPE_FIELD, isScopeEmpty, parentLevel } from '../../overview/models/hierarchyScope';

const FALLBACK = 'Unable to load the hierarchy options. Please try again.';

const PLURAL: Readonly<Record<HierarchyLevel, string>> = {
  entities: 'Entities',
  'business-units': 'Business Units',
  divisions: 'Divisions',
  departments: 'Departments',
};

interface LevelOptions {
  options: HierarchyNode[];
  loading: boolean;
  /** Loaded, without error, for the parents selected now -- the only state a selection may be pruned against. */
  ready: boolean;
  error: string | null;
}

/**
 * One level's options for the selected parents, re-read whenever they change.
 * The previous answer stays on screen while the next one loads, so the chips of
 * a kept selection do not flicker out.
 */
function useLevelOptions(level: HierarchyLevel, parentIds: readonly number[], enabled = true): LevelOptions {
  const key = enabled ? parentIds.join(',') : '-';
  const [state, setState] = useState<{ key: string | null; options: HierarchyNode[]; error: string | null }>({
    key: null,
    options: [],
    error: null,
  });

  useEffect(() => {
    if (key === '-') return undefined;
    let live = true;
    const ids = key === '' ? [] : key.split(',').map(Number);
    listHierarchyOptions(level, ids).then(
      (options) => {
        if (live) setState({ key, options, error: null });
      },
      (error: unknown) => {
        if (live) setState({ key, options: [], error: getErrorMessage(normalizeError(error)) || FALLBACK });
      },
    );
    return () => {
      live = false;
    };
  }, [level, key]);

  if (key === '-') return { options: [], loading: false, ready: false, error: null };
  return {
    options: state.options,
    loading: state.key !== key,
    ready: state.key === key && state.error === null,
    error: state.key === key ? state.error : null,
  };
}

/** Keeps, below `from`, only the values whose parent is still selected -- by the parents the options name. */
function pruneBelow(scope: HierarchyScope, from: HierarchyLevel, known: Readonly<Record<HierarchyLevel, LevelOptions>>) {
  let next = scope;
  for (const level of HIERARCHY_LEVELS.slice(HIERARCHY_LEVELS.indexOf(from) + 1)) {
    const above = parentLevel(level);
    if (!above) continue;
    const parents = next[SCOPE_FIELD[above]];
    const field = SCOPE_FIELD[level];
    const kept = next[field].filter((id) => {
      const node = known[level].options.find((option) => option.id === id);
      /* A value the options do not name yet is left for the reload to decide. */
      return node?.parentId == null || parents.includes(node.parentId);
    });
    if (kept.length !== next[field].length) next = { ...next, [field]: kept };
  }
  return next;
}

/** `BU-001 – Procurement`; a Division or Department adds its parent's code: `DIV-001 – Operations (BU-001)`. */
function named(level: HierarchyLevel, node: HierarchyNode): string {
  const own = node.code ? `${node.code} – ${node.name}` : node.name;
  return (level === 'divisions' || level === 'departments') && node.parentCode ? `${own} (${node.parentCode})` : own;
}

/**
 * Every chosen parent whose children have loaded and include none chosen -- each parent on its own, as the server
 * checks it. A parent with no children needs nothing; a level whose options are still loading is not judged.
 */
function missingChildren(
  value: HierarchyScope,
  entityIds: readonly number[],
  levels: Readonly<Record<HierarchyLevel, LevelOptions>>,
): HierarchyIssues {
  const issues: HierarchyIssues = {};
  for (const level of DRAWN) {
    const above = parentLevel(level);
    if (!above || !levels[level].ready) continue;
    const parents = level === 'business-units' ? entityIds : value[SCOPE_FIELD[above]];
    const chosen = new Set(value[SCOPE_FIELD[level]]);
    const messages: string[] = [];
    for (const parentId of parents) {
      const children = levels[level].options.filter((option) => option.parentId === parentId);
      if (children.length === 0 || children.some((child) => chosen.has(child.id))) continue;
      const parent = levels[above].options.find((option) => option.id === parentId);
      /* An entity not (yet) among the read options is named by the code its units carry. */
      const name = parent ? named(above, parent) : (children[0]?.parentCode ?? `#${String(parentId)}`);
      messages.push(`Please select a ${HIERARCHY_LEVEL_LABEL[level]} for ${HIERARCHY_LEVEL_LABEL[above]} ${name}.`);
    }
    if (messages.length > 0) issues[level] = messages;
  }
  return issues;
}

/**
 * Every `required` level left empty whose options have loaded and offer something -- "Please select a Division."
 * A level whose chosen parents offer nothing (or that has no parent chosen yet) is not judged.
 */
function emptyRequired(
  value: HierarchyScope,
  required: readonly HierarchyLevel[],
  levels: Readonly<Record<HierarchyLevel, LevelOptions>>,
): HierarchyIssues {
  const issues: HierarchyIssues = {};
  for (const level of DRAWN) {
    if (!required.includes(level) || value[SCOPE_FIELD[level]].length > 0) continue;
    if (!levels[level].ready || levels[level].options.length === 0) continue;
    issues[level] = [`Please select a ${HIERARCHY_LEVEL_LABEL[level]}.`];
  }
  return issues;
}

interface HierarchyMultiSelectProps {
  /** Distinguishes two instances' input ids on one page. */
  idPrefix: string;
  value: HierarchyScope;
  onChange: (next: HierarchyScope) => void;
  disabled?: boolean;
  /** Levels drawn with the required star; the server holds the rule. */
  required?: readonly HierarchyLevel[];
  /** A message under a level, keyed by level -- a refused save's `scope.<field>` errors. */
  errors?: Partial<Record<HierarchyLevel, string>>;
  /** `filter`: a listing's filter bar -- no stars, compact placeholders, no completeness check. */
  variant?: 'form' | 'filter';
  /**
   * Show what the selection still needs under each level ("Please select a Division for Business Unit …").
   * A form turns it on once its user tries to save or submit; until then nothing is flagged.
   */
  showValidation?: boolean;
  /** Told whenever what the selection still needs changes, so a form can refuse to save or submit. */
  onIssuesChange?: (issues: HierarchyIssues) => void;
  /**
   * Offer only these values at each level that names some -- a supplier is onboarded under the hierarchy its
   * template is mapped to, not the entity's whole (2026-10-08). A level left empty here is offered whole.
   */
  within?: HierarchyScope;
  /**
   * Whether an empty level takes its first option (formerly the Default Template screens, removed 2026-10-16). Off, every level opens as it is
   * given -- the PR Template and Supplier Onboarding builders, the Purchase Requests and Supplier master screens
   * (2026-10-09).
   */
  defaultFirst?: boolean;
  /** Whether a chosen parent with children asks for one of its own (`form` only). Off for an optional hierarchy. */
  checkChildren?: boolean;
  /**
   * Whether a `required` level left empty is an issue ("Please select a Division."), not only a star (`form` only):
   * an Admin's request on the levels its template leaves unmapped, and a supplier's hierarchy on Use Template
   * (2026-10-09).
   */
  enforceRequired?: boolean;
  /**
   * Levels drawn disabled while the others stay editable: an Initiator's request keeps the levels its template maps
   * and chooses the rest (2026-10-09).
   */
  lockedLevels?: readonly HierarchyLevel[];
  /**
   * Further cells of the same row grid after Department -- New Purchase Request's Category filter (2026-10-09), so
   * it is one column wide like the three levels rather than a row of its own.
   */
  trailing?: ReactNode;
}

/** A level's options narrowed to ids (see within); all of them when none are named. */
function limitTo(level: LevelOptions, ids: readonly number[] | undefined): LevelOptions {
  if (!ids || ids.length === 0) return level;
  return { ...level, options: level.options.filter((option) => ids.includes(option.id)) };
}

/** The levels drawn: the Entity is the one the shell's picker (top left) has the user working in. */
const DRAWN: readonly HierarchyLevel[] = ['business-units', 'divisions', 'departments'];

/**
 * Business Unit → Division → Department, each a multi-select, under the entity
 * selected in the shell's entity picker.
 *
 * There is no Entity dropdown: the entity is the one every request is already
 * scoped to (`X-Entity-Id`), and the selection's `legalEntityIds` is kept equal
 * to it -- switching the picker re-maps the selection to the new entity. A user
 * with no entity selected (a Mega Admin, who has no picker) is offered the
 * units of every entity, and the entities are those of the units chosen.
 *
 * Each level offers the children of every value selected above it, read from
 * the server and labelled there (`CODE + Name (Parent Code)`). **The first value
 * of each level is selected by default** whenever its options arrive for a new
 * set of parents and nothing is selected there -- so a form opens on the first
 * Business Unit, its first Division and that Division's first Department --
 * unless `defaultFirst` is off (the PR and Supplier Onboarding screens), when
 * nothing is chosen for the user. A level the user clears stays clear until its
 * parents change. A selection the
 * control mounts with (an Edit) is kept as it is.
 *
 * Changing a level keeps the selections below it that still sit under a
 * selected parent and drops the rest; once the new options arrive, anything
 * they no longer contain (another parent's, or retired) is dropped too.
 *
 * Drawn as masters' record-form fields -- `ms-field` cells in an
 * `ms-record__grid`, PrimeReact `MultiSelect`s with chips, each chip's own
 * remove control the one way a chosen value is cancelled (no field-wide clear
 * beside it), `ms-panel` lists.
 */
export function HierarchyMultiSelect({
  idPrefix,
  value,
  onChange,
  disabled = false,
  required = [],
  errors = {},
  variant = 'form',
  showValidation = false,
  onIssuesChange,
  within,
  defaultFirst = true,
  checkChildren = true,
  enforceRequired = false,
  lockedLevels = [],
  trailing,
}: HierarchyMultiSelectProps) {
  const selectedEntityId = useAppSelector((state) => state.selectedEntity.id);
  /* Every entity the caller may use: the units' parents without a picker, and the names messages give. */
  const entities = useLevelOptions('entities', []);
  const entityIds = selectedEntityId !== null ? [selectedEntityId] : entities.options.map((option) => option.id);
  const businessUnits = limitTo(useLevelOptions('business-units', entityIds), within?.businessUnitIds);
  const divisions = limitTo(useLevelOptions('divisions', value.businessUnitIds), within?.divisionIds);
  const departments = limitTo(useLevelOptions('departments', value.divisionIds), within?.departmentIds);
  const levels: Record<HierarchyLevel, LevelOptions> = {
    entities,
    'business-units': businessUnits,
    divisions,
    departments,
  };
  const parentKeys: Record<HierarchyLevel, string> = {
    entities: '',
    'business-units': entityIds.join(','),
    divisions: value.businessUnitIds.join(','),
    departments: value.divisionIds.join(','),
  };

  /* The latest selection and handler, for the effect below without making it re-run on every keystroke. */
  const latest = useRef({ value, onChange, disabled, defaultFirst });
  latest.current = { value, onChange, disabled, defaultFirst };

  /*
    The parents each level last defaulted for. A selection the control mounts with is the author's (an Edit, or
    the builder's step 1 drawn again), so the parents it mounts under count as done.
  */
  const defaulted = useRef<Partial<Record<HierarchyLevel, string>>>(
    isScopeEmpty(value) ? {} : { ...parentKeys },
  );

  /*
    One pass, so the entity, the prune and the defaults cannot overwrite one another within a render:
    1. the entity follows the shell's picker (or, without one, the chosen units' entities);
    2. a selected value the loaded options do not contain is dropped;
    3. a level that is empty, under parents it has not defaulted for yet, takes its first option.
  */
  const readyKey = HIERARCHY_LEVELS.map((level) =>
    levels[level].ready ? `${parentKeys[level]}:${levels[level].options.map((o) => o.id).join('.')}` : '-',
  ).join('|');
  useEffect(() => {
    const current = latest.current.value;
    let next = current;

    const wantedEntities =
      selectedEntityId !== null
        ? [selectedEntityId]
        : [
            ...new Set(
              next.businessUnitIds
                .map((id) => businessUnits.options.find((option) => option.id === id)?.parentId)
                .filter((id): id is number => typeof id === 'number'),
            ),
          ];
    if (wantedEntities.join(',') !== next.legalEntityIds.join(',')) next = { ...next, legalEntityIds: wantedEntities };

    for (const level of DRAWN) {
      if (!levels[level].ready) continue;
      const field = SCOPE_FIELD[level];
      const offered = new Set(levels[level].options.map((option) => option.id));
      const kept = next[field].filter((id) => offered.has(id));
      if (kept.length !== next[field].length) next = { ...next, [field]: kept };

      if (defaulted.current[level] !== parentKeys[level]) {
        defaulted.current[level] = parentKeys[level];
        const first = levels[level].options[0];
        if (latest.current.defaultFirst && !latest.current.disabled && next[field].length === 0 && first) {
          next = { ...next, [field]: [first.id] };
          /* The level below has new parents now; it defaults once their options arrive. */
          break;
        }
      }
    }
    if (next !== current) latest.current.onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by what the options are, see readyKey
  }, [readyKey, selectedEntityId, value.businessUnitIds.join(',')]);

  const change = useCallback(
    (level: HierarchyLevel, ids: number[]) => {
      onChange(pruneBelow({ ...value, [SCOPE_FIELD[level]]: ids }, level, levels));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `levels` is rebuilt each render from the four hooks
    [onChange, value, entities.options, businessUnits.options, divisions.options, departments.options],
  );

  const error = HIERARCHY_LEVELS.map((level) => levels[level].error).find((message) => message !== null) ?? null;

  /* What the selection still needs, re-judged on every change -- a fixed level's message goes with it. */
  const issues: HierarchyIssues =
    variant !== 'form'
      ? {}
      : {
          ...(checkChildren ? missingChildren(value, entityIds, levels) : {}),
          ...(enforceRequired ? emptyRequired(value, required, levels) : {}),
        };
  const issuesKey = JSON.stringify(issues);
  const report = useRef(onIssuesChange);
  report.current = onIssuesChange;
  /*
    A layout effect, so the form knows within the same commit: a Save clicked the moment the defaults land must not
    be judged against the selection before them.
  */
  useLayoutEffect(() => {
    report.current?.(JSON.parse(issuesKey) as HierarchyIssues);
  }, [issuesKey]);

  /* A chip's tooltip and its remove control's name, from its option's label. */
  const chipPt = (level: HierarchyLevel) => {
    const labelOf = (id: unknown) =>
      levels[level].options.find((option) => option.id === id)?.label ?? String(id);
    const at = (options: unknown) => (options as { context?: { value?: unknown } } | undefined)?.context?.value;
    return {
      tokenLabel: (options: unknown) => ({ title: labelOf(at(options)) }),
      removeTokenIcon: (options: unknown) => ({ 'aria-label': `Remove ${labelOf(at(options))}` }),
    };
  };

  return (
    <div className="ms-record__grid ms-record__grid--row">
      <Suspense fallback={null}>
        <LazyMasterScreenStyles />
      </Suspense>
      {DRAWN.map((level) => {
        const above = parentLevel(level);
        const noParent =
          level === 'business-units' ? entityIds.length === 0 : above !== null && value[SCOPE_FIELD[above]].length === 0;
        const inputId = `${idPrefix}-${level}`;
        /* An Entity has no control of its own here, so a refused entity is reported under Business Unit. */
        const server = errors[level] ?? (level === 'business-units' ? errors.entities : undefined);
        const messages = showValidation && issues[level]?.length ? (issues[level] ?? []) : server ? [server] : [];
        const message = messages.length > 0 ? messages.join(' ') : undefined;
        const star = variant === 'form' && required.includes(level);
        return (
          <div className="ms-field" key={level}>
            <label className="ms-field__label" htmlFor={inputId}>
              {HIERARCHY_LEVEL_LABEL[level]}
              {star ? (
                <span className="ms-field__required" aria-hidden="true">
                  {' '}
                  *
                </span>
              ) : null}
            </label>
            <MultiSelect
              inputId={inputId}
              value={value[SCOPE_FIELD[level]]}
              options={levels[level].options}
              optionLabel="label"
              optionValue="id"
              display="chip"
              filter
              showSelectAll={false}
              disabled={disabled || noParent || lockedLevels.includes(level)}
              placeholder={
                noParent && above
                  ? level === 'business-units'
                    ? 'No entity selected'
                    : `Select ${HIERARCHY_LEVEL_LABEL[above]} first`
                  : variant === 'filter'
                    ? `All ${PLURAL[level]}`
                    : `Select ${PLURAL[level]}`
              }
              emptyMessage={levels[level].loading ? 'Loading…' : `No active ${PLURAL[level].toLowerCase()}`}
              className={message ? 'p-invalid w-full' : 'w-full'}
              panelClassName="ms-panel ms-panel--std"
              dropdownIcon="pi pi-chevron-down"
              pt={chipPt(level) as never}
              aria-invalid={message ? true : undefined}
              aria-describedby={message ? `${inputId}-error` : undefined}
              onChange={(event) => {
                change(level, (event.value as number[] | null) ?? []);
              }}
            />
            {messages.length > 0 ? (
              <div id={`${inputId}-error`} className="ms-field__errors" role="alert">
                {messages.map((text) => (
                  <small key={text} className="ms-field__error">
                    {text}
                  </small>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
      {trailing}
      {error ? (
        <p className="ms__error m-0 ms-field--full" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}